import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../supabaseClient';

const OptOut = () => {
  const [formData, setFormData] = useState({
    phone_number: '',
    email: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const messageRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const { error } = await supabase
        .from('opt_out_requests')
        .insert([{
          phone_number: formData.phone_number,
          email: formData.email,
          reason: formData.reason,
          status: 'pending',
          source: 'rmq_digital',
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      setSubmitMessage('Your opt-out request has been processed successfully. You will no longer receive SMS messages from us within 24 hours.');
      setFormData({
        phone_number: '',
        email: '',
        reason: ''
      });
      
      // Scroll to success message
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } catch (error) {
      setSubmitMessage('There was an error processing your opt-out request. Please email optout@rmqdigital.com or try again.');
      console.error('Supabase Error:', error);
      
      // Scroll to error message
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{paddingTop: '100px'}}>
      <Helmet>
        <title>SMS Opt-Out - Unsubscribe from Text Messages | RMQ Digital</title>
        <meta name="description" content="Unsubscribe from SMS messages. Reply STOP to any text or submit opt-out request. Processed within 24 hours. Email: optout@rmqdigital.com" />
        <meta name="keywords" content="SMS opt-out, unsubscribe, STOP keyword, text message unsubscribe, opt-out request" />
      </Helmet>
      
      <section style={{padding: '4rem 2rem', background: '#f9fafb'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h1 style={{fontSize: '3rem', fontWeight: '800', marginBottom: '1rem', color: '#1f2937', fontFamily: 'Space Grotesk'}}>SMS Opt-Out Request</h1>
          
          <div style={{maxWidth: '600px', margin: '0 auto'}}>
            <div style={{background: '#ede9fe', padding: '2rem', borderRadius: '12px', marginBottom: '2rem', border: '2px solid #8b5cf6'}}>
              <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.5rem'}}>Unsubscribe from SMS Messages</h2>
              <p style={{color: '#5b21b6'}}>
                We respect your communication preferences. If you no longer wish to receive SMS messages from 
                RMQ Digital, please complete the form below or reply STOP to any text message.
              </p>
            </div>

            {submitMessage && (
              <div 
                ref={messageRef}
                style={{
                  background: submitMessage.includes('error') ? '#fef2f2' : '#d1fae5',
                  color: submitMessage.includes('error') ? '#991b1b' : '#065f46',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  marginBottom: '1.5rem',
                  border: `2px solid ${submitMessage.includes('error') ? '#ef4444' : '#10b981'}`,
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              >
                {submitMessage}
              </div>
            )}

            <div style={{background: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)'}}>
              <h2 style={{color: '#1f2937', marginBottom: '1.5rem', fontSize: '1.25rem'}}>Opt-Out Request Form</h2>
              
              <form onSubmit={handleSubmit}>
                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="Enter the phone number to opt out"
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                  />
                  <small style={{color: '#6b7280', fontSize: '0.875rem'}}>Include country code if applicable (e.g., +1 for US numbers)</small>
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                    Email Address (Optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your email for confirmation"
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                    Reason for Opting Out (Optional)
                  </label>
                  <select
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select a reason</option>
                    <option value="too_frequent">Messages too frequent</option>
                    <option value="not_relevant">Content not relevant</option>
                    <option value="never_subscribed">Never subscribed</option>
                    <option value="privacy_concerns">Privacy concerns</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <button 
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: isSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #6d28d9, #8b5cf6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSubmitting ? 'Processing...' : 'Submit Opt-Out Request'}
                </button>
              </form>
            </div>

            <div style={{background: '#fef2f2', padding: '2rem', borderRadius: '12px', marginTop: '2rem', border: '2px solid #ef4444'}}>
              <h3 style={{color: '#dc2626', marginBottom: '1rem', fontSize: '1.125rem'}}>Alternative Opt-Out Methods</h3>
              <ul style={{lineHeight: '1.8', paddingLeft: '1.5rem', color: '#991b1b'}}>
                <li><strong>Text STOP:</strong> Reply STOP to any SMS message</li>
                <li><strong>Email:</strong> optout@rmqdigital.com</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OptOut;
